/* eslint-disable max-len */
import React from "react";
import styles from "./specialties.module.scss";
import { IllustratedTextListComponent } from "../../components/illustrated-text-list/IllustratedTextList.component";
import Architecture from "@assets/images/architecture.jpg";
import Cloud from "@assets/images/cloud.jpg";
import Integrate from "@assets/images/integrate.jpg";
import UX from "@assets/images/ux.jpg";
import AI from "@assets/images/ai.jpg";
import Blockchain from "@assets/images/blockchain.jpg";
import { InformationComponent } from "../../components/information/information.component";
import { spaceshipIcon } from "../../utils/svg";

export const Specialties: React.FC = () => {
  return (
    <div className={styles["specialties"]}>
      <InformationComponent
        icon={spaceshipIcon}
        sizeX={543}
        sizeY={543}
        kicker="FLIGHT LOG"
        title="The domains I have ventured into.">
        <JourneyIntro />
      </InformationComponent>
      <IllustratedTextListComponent items={items} />
    </div>
  );
};

const JourneyIntro: React.FC = () => {
  return (
    <div className={styles["column"]}>
      <blockquote>
        Each section covers a domain I have worked in: what I have learned,
        where I have seen it applied best, and where I think it should go next.
      </blockquote>
      <p>I hope these chronicles prove useful to you.</p>
    </div>
  );
};

const softwareArchitecture: React.FC = () => {
  return (
    <>
      <blockquote>
        Architecture takes many shapes and forms. A solid architectural
        foundation is crucial for any software project, but there is&nbsp;
        <strong>no one-size-fits-all</strong>.
      </blockquote>
      <p>{`Team size, maturity and application complexity should each shape the approach.`}</p>
      <p>{`The right architecture lets an organisation reach its goals while staying ready for growth and change. Microservices, event-driven architecture and serverless computing each have their place. Choosing between them means weighing factors like performance, security and maintainability for the project at hand.`}</p>
      <p>
        {`The best architectures I have seen were `}
        <strong>built collaboratively</strong>
        {`. When stakeholders, developers and other team members all get to shape the design, the result aligns with what everyone actually needs, and the process itself fosters a positive and supportive working environment.`}
      </p>
    </>
  );
};

const devops: React.FC = () => {
  return (
    <>
      <blockquote>
        The cloud is no longer a differentiator;&nbsp;
        <strong>it is the default</strong>.
      </blockquote>
      <p>{`What sets teams apart today is everything around it: the automation that carries an idea from commit to production without friction.`}</p>
      <p>
        {`I have designed continuous integration and continuous delivery pipelines that build, test and ship changes automatically, with quality gates, security checks and rollback paths built in. The goal is always the same: `}
        <strong>releases so routine they become boring</strong>.
      </p>
      <p>
        {`Infrastructure as code ties it together. When environments live in version-controlled templates they become reproducible, reviewable and disposable: a full environment for a feature branch is `}
        <strong>a command rather than a project</strong>
        {`, and recovery is a redeploy rather than a rescue operation.`}
      </p>
      <p>{`Combined with on-demand cloud resources, this way of working delivers speed with reliability: small changes shipped often, failures caught early, and teams spending their time on the product instead of the plumbing.`}</p>
    </>
  );
};

const integration: React.FC = () => {
  return (
    <>
      <blockquote>Modern systems rarely live alone.</blockquote>
      <p>
        {`Integrating applications, systems and data sources cleanly is what turns a collection of tools into `}
        <strong>a cohesive solution</strong>
        {` that drives value and supports an organisation's goals.`}
      </p>
      <p>{`I have designed and implemented integrations across a wide range of protocols, from GraphQL, RPC and RESTful APIs to SOAP, message queues, websockets and event-driven architectures, always aiming for reliable, secure and maintainable communication between diverse systems.`}</p>
      <p>{`Every successful integration starts with understanding the unique requirements and constraints of each system involved. Working across technologies, platforms and programming languages lets me adapt to the specific needs of each project, functional and non-functional alike.`}</p>
      <p>
        {`Data is where integration truly shines. Robust data pipelines, ETL (Extract, Transform, Load) processes and `}
        <strong>real-time synchronisation</strong>
        {` unlock insights from vast data repositories and enable data-driven decisions, all without compromising live application performance.`}
      </p>
    </>
  );
};

const design: React.FC = () => {
  return (
    <>
      <blockquote>
        My background is primarily technical, but I have always been passionate
        about building things people enjoy using.
      </blockquote>
      <p>{`Efficient and functional, certainly, but also visually appealing.`}</p>
      <p>
        {`Throughout my career I have developed a keen understanding of user experience (UX) principles and design methodologies. `}
        <strong>Empathising with users</strong>
        {` and taking their needs and expectations into account is the key to intuitive, accessible and engaging interfaces. Striking the right balance between `}
        <strong>aesthetics and usability</strong>
        {` makes a product both pleasing to look at and easy to navigate.`}
      </p>
      <p>{`Good UX design comes from close collaboration between designers, developers and stakeholders. Open communication and honest feedback keep a design aligned with the project's objectives and its intended audience, and make the work more enjoyable for everyone involved.`}</p>
      <p>{`Tools like Figma, Sketch and Adobe Creative Suite help bring design ideas to life, and keep designers and developers working from the same page.`}</p>
    </>
  );
};

const ai: React.FC = () => {
  return (
    <>
      <blockquote>
        Artificial intelligence has moved from the research lab into everyday
        products, and it is reshaping every industry it touches.
      </blockquote>
      <p>{`A foundation in classical machine learning, from regression and decision trees to deep learning and neural networks, still matters: it anchors my work with the current generation of AI, from large language models and generative systems to agents that reason over tools and data.`}</p>
      <p>
        {`Turning AI into value is `}
        <strong>an engineering discipline</strong>
        {`. Retrieval-augmented generation, fine-tuning, prompt design and rigorous evaluation separate an impressive demo from a dependable system, whether building on in-house models or on today's rapidly improving AI APIs.`}
      </p>
      <p>
        {`Used well, AI `}
        <strong>multiplies a team&apos;s efficiency</strong>
        {` and productivity at every level. It is rapidly becoming a competitive necessity rather than an advantage.`}
      </p>
    </>
  );
};

const blockchain: React.FC = () => {
  return (
    <>
      <blockquote>
        Blockchain is a field I am deeply passionate about: a transformative
        technology with the potential to revolutionise industries and enable a
        fairer, more decentralised future.
      </blockquote>
      <p>{`My fascination began in the early days of cryptocurrencies and grew as I explored smart contracts and the ecosystems they could create. Since then I have worked on a wide range of use cases, from supply chain management and decentralised finance to self-sovereign identity and web3 applications (dApps), on both permissioned and public chains. That breadth has given me a grounded understanding of blockchain's potential and of the challenges it still faces.`}</p>
      <p>{`The advantages are real:`}</p>
      <ul>
        <li>
          Enhanced security through decentralisation and tamper resistance
        </li>
        <li>Transparency through an open, distributed ledger</li>
        <li>
          Efficiency through peer-to-peer transactions with a&nbsp;
          <strong>single source of truth</strong> and no intermediaries
        </li>
      </ul>
      <p>{`Immutability and traceability build trust between parties, allowing business networks to open up and grow. And programmable smart contracts unlock automation and innovation in areas as varied as governance, supply chain management, intellectual property and HR.`}</p>
      <p>
        {`Above all, blockchain is an essential component for implementing digital identity in a fair and `}
        <strong>self-sovereign</strong>
        {` manner. This is of vital importance to protect human rights, norms and values as we transcend further into the digital age.`}
      </p>
    </>
  );
};

// Per-row layout tuning, matched to each row's copy volume ("its specific
// conditions"): wideArt hands more card width to the panel when the
// beside-copy runs short; besideBlocks caps how many leading blocks share
// the row with the panel — the rest clear below it (see IllustratedTextList).
const items = [
  {
    image: Architecture,
    kicker: "FOUNDATIONS",
    title: "Software Architecture",
    text: softwareArchitecture,
    // The long lead quote fills the beside column on its own.
    wideArt: true,
    besideBlocks: 1 as const,
  },
  {
    image: Cloud,
    kicker: "AUTOMATION",
    title: "Cloud Automation & DevOps",
    text: devops,
    // Two-line quote: two short paragraphs join it beside the panel.
    besideBlocks: 3 as const,
  },
  {
    image: Integrate,
    kicker: "CONNECTIVITY",
    title: "Integration",
    text: integration,
    // One-line quote: two paragraphs join it beside the panel.
    besideBlocks: 3 as const,
  },
  {
    image: UX,
    kicker: "HUMAN INTERFACE",
    title: "Design",
    text: design,
    besideBlocks: 3 as const,
  },
  {
    image: AI,
    kicker: "INTELLIGENCE",
    title: "Artificial Intelligence",
    text: ai,
    wideArt: true,
    besideBlocks: 2 as const,
  },
  {
    image: Blockchain,
    kicker: "DECENTRALISATION",
    title: "Blockchain",
    text: blockchain,
    // Long quote plus the longest paragraph carry the beside column.
    besideBlocks: 2 as const,
  },
];
